"use client";

import { useSearchParams } from "next/navigation";
import {
  AcademicCapIcon,
  ArrowTopRightOnSquareIcon,
  CalculatorIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

import { OperationalPage, Surface } from "@/app/Components/OperationalPage";
import { Tabs } from "@/app/Components/Tabs";
import { PreviousClassesPanel } from "@/app/profile/ProfileWorkspaceView";
import { createProfileWorkspaceFixture } from "@/app/profile/profileWorkspace.fixtures";
import dynamic from 'next/dynamic';
const PlanningFeedbackFixture = dynamic(() => import('./PlanningFeedbackFixture').then(module => module.PlanningFeedbackFixture));
import { PlanningEmbeddedWorkspace } from "./PlanningEmbeddedWorkspace";
import {
  buildPlanningHref,
  resolvePlanView,
  type PlanView,
} from "./planningNavigation";

const VIEW_TABS = [
  { id: "roadmap", label: "Roadmap", icon: <AcademicCapIcon className="h-4 w-4" /> },
  { id: "requirements", label: "Prerequisites", icon: <ClipboardDocumentCheckIcon className="h-4 w-4" /> },
  { id: "ap", label: "AP credit", icon: <AcademicCapIcon className="h-4 w-4" /> },
  { id: "transfer", label: "Transfer credit", icon: <ArrowTopRightOnSquareIcon className="h-4 w-4" /> },
  { id: "record", label: "Academic record", icon: <DocumentTextIcon className="h-4 w-4" /> },
  { id: "gpa", label: "GPA", icon: <CalculatorIcon className="h-4 w-4" /> },
] as const;

export default function PlanningWorkspace() {
  const searchParams = useSearchParams();
  const activeView = resolvePlanView(searchParams.get("view"));
  const navigationFixture = process.env.NODE_ENV === "development"
    && process.env.NEXT_PUBLIC_ENABLE_AUDIT_FIXTURES === "1"
    && searchParams.get("auditFixture") === "plan-navigation";
  const tabs = <Tabs
    label="Plan workspace views"
    activeId={activeView}
    onChange={(id) => window.history.pushState(null, '', buildPlanningHref(id as PlanView, new URLSearchParams(window.location.search)))}
    items={[...VIEW_TABS]}
  />;

  if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_ENABLE_AUDIT_FIXTURES === '1' && searchParams.get('auditFixture') === 'planning-feedback') {
    return <OperationalPage className="pb-28 pt-3" data-audit-ready="planning-feedback-fixture"><h1 className="sr-only">Plan</h1><div className="border-b border-line">{tabs}</div><p className="my-3 text-xs text-content-muted">Synthetic planning preview · browser-only edits · no student account</p><PlanningFeedbackFixture view={activeView} /></OperationalPage>;
  }

  if (navigationFixture) {
    const titles: Record<PlanView, string> = {
      roadmap: "Degree plan roadmap",
      requirements: "Check course prerequisites",
      ap: "Review AP credit",
      transfer: "Explore transfer alternatives",
      record: "Academic record",
      gpa: "Project your GPA",
    };
    const descriptions: Record<PlanView, string> = {
      roadmap: "Choose a saved plan to organize courses by term and track open requirements.",
      requirements: "Check a course against completed coursework, applied AP equivalents, and prerequisite overrides.",
      ap: "Review and manage Advanced Placement credit alongside your academic record.",
      transfer: "Compare published NJ Transfer evaluations for the selected Rutgers school and review conditions for remaining requirements.",
      record: "Review completed coursework and grades used by planning tools.",
      gpa: "Compare projected grades against supported GPA inputs.",
    };
    return <OperationalPage className="mx-auto w-full max-w-[1860px] pb-28 pt-3" data-audit-ready="plan-navigation-fixture">
      <h1 className="sr-only">Plan</h1>
      <div className="shrink-0 border-b border-[var(--ss-border)] px-1">{tabs}</div>
      <Surface variant="feature" className="mt-5 p-5">
        <p className="operational-eyebrow">Plan navigation preview</p>
        <h2 className="operational-title">{titles[activeView]}</h2>
        <p className="operational-description">{descriptions[activeView]}</p>
        <p className="mt-4 text-xs text-content-muted">Owner-free development preview. No student record or saved plan is loaded.</p>
      </Surface>
    </OperationalPage>;
  }

  const academicRecordFixture = process.env.NODE_ENV === "development"
    && process.env.NEXT_PUBLIC_ENABLE_AUDIT_FIXTURES === "1"
    && searchParams.get("auditFixture") === "gpa-empty"
    && activeView === "record";
  if (academicRecordFixture) {
    const profileFixture = createProfileWorkspaceFixture("previous_classes");
    return <OperationalPage className="mx-auto w-full max-w-[1860px] pb-28 pt-3" data-audit-ready="plan-record-audit-fixture">
      <h1 className="sr-only">Plan</h1>
      <div className="shrink-0 border-b border-[var(--ss-border)] px-1">{tabs}</div>
      <Surface variant="feature" className="mt-5 p-5">
        <p className="operational-eyebrow">Owner-free academic record preview</p>
        <p className="operational-description">Synthetic example content; no student record is loaded or changed.</p>
      </Surface>
      <div className="mt-5"><PreviousClassesPanel {...profileFixture.previousClasses} /></div>
    </OperationalPage>;
  }

  return (
    <OperationalPage className="mx-auto w-full max-w-[1860px] pb-28 pt-3" data-audit-ready="planning-workspace-ready">
      <h1 className="sr-only">Plan</h1>
      <div className="shrink-0 border-b border-[var(--ss-border)] px-1">
        {tabs}
      </div>

      <div className="mt-5" role="tabpanel" aria-label={`${activeView} planning view`}>
        <PlanningEmbeddedWorkspace view={activeView} />
      </div>
    </OperationalPage>
  );
}
